/* eslint-disable @typescript-eslint/no-unsafe-assignment */
import React, { useCallback, useEffect, useState, memo } from 'react';
import { Box, Button, Flex, Menu, MenuButton, MenuItem, MenuList, Text } from '@chakra-ui/core';
import { MdArrowForwardIos } from 'react-icons/md';
import { useTranslation } from 'react-i18next';
import map from 'lodash/map';
import { useStoreActions, useStoreState } from '../../../../models/hooks';
import { ToDoList } from '../../../../firebase/firestore/documents/toDo';
import colors from '../../../../styles/colors';
import { StatusEnum, TodoStatusAssignedByType } from '../../../dashboard/RecruiterTodoListType';
import { CandidateRecruiterAction } from '../../../../firebase/firestore/documents/candidateRecruiterAction';

const getButtonStyles = (givenStatus: StatusEnum): { bg: string; color: string } => {
  switch (givenStatus) {
    case StatusEnum.TODO:
      return {
        bg: colors.blue[20],
        color: colors.blue[500],
      };
    case StatusEnum.IN_PROGRESS:
      return {
        bg: '#D69E2E1A',
        color: colors.californiaGold,
      };
    case StatusEnum.COMPLETED:
      return {
        bg: '#ebf6f0',
        color: '#38A169',
      };
    default:
      return {
        bg: colors.neutralGray[100],
        color: 'gray.500',
      };
  }
};

export type CandidateStatusDropdownProps = {
  id: string;
  status: string;
  todoStatus: StatusEnum;
  candidateId: string;
  category: string;
  setToDoList: React.Dispatch<React.SetStateAction<ToDoList>>;
  statusAssignedBy: TodoStatusAssignedByType | undefined;
  currentDoc?: CandidateRecruiterAction;
};

export const CandidateStatusDropdown = memo(function CandidateStatusDropdown({
  id,
  candidateId,
  status,
  todoStatus,
  category,
  setToDoList,
  currentDoc,
}: CandidateStatusDropdownProps): JSX.Element {
  const [taskStatus, setTaskStatus] = useState<StatusEnum>(todoStatus ?? StatusEnum.TODO);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const { updateStatus } = useStoreActions((actions) => actions.todo);
  const user = useStoreState((state) => state.app.user);
  const { t } = useTranslation('toDoList');

  const actionLabels = {
    [StatusEnum.TODO]: t('toDo'),
    [StatusEnum.IN_PROGRESS]: t('inProgress'),
    [StatusEnum.COMPLETED]: t('completed'),
  };

  const actionBtnLabel = t(actionLabels[taskStatus]);

  const setNewTodoList = useCallback(
    (actionStatusLabel: StatusEnum, givenId: string) => {
      setToDoList((prevState) => {
        const data = map(prevState.data, (item) => {
          if (item.recruiterActionId === givenId) {
            return { ...item, todoStatus: actionStatusLabel };
          }
          return item;
        });

        return {
          ...prevState,
          count: prevState.count,
          data,
        };
      });
    }, // eslint-disable-next-line react-hooks/exhaustive-deps
    [],
  );

  const updateCandidateRecruiterAction = async (actionStatusLabel: StatusEnum) => {
    if (user?.id) {
      setIsLoading(true);
      await updateStatus({
        userId: user.id,
        docId: `${id}`,
        status: actionStatusLabel,
        category,
        subCategory: status,
        candidateId,
      })
        .then(() => {
          if (actionStatusLabel === StatusEnum.IN_PROGRESS) {
            setTaskStatus(actionStatusLabel);
          } else {
            setTaskStatus(actionStatusLabel === StatusEnum.TODO ? StatusEnum.TODO : StatusEnum.COMPLETED);
          }
          setNewTodoList(actionStatusLabel, id);
        })
        .finally(() => {
          setIsLoading(false);
        });
    }
  };

  useEffect(() => {
    if (currentDoc) {
      if (currentDoc && currentDoc.status === StatusEnum.COMPLETED) {
        setTaskStatus(StatusEnum.COMPLETED);
        setNewTodoList(StatusEnum.COMPLETED, id);
      } else if (currentDoc && currentDoc.status === StatusEnum.IN_PROGRESS) {
        setTaskStatus(StatusEnum.IN_PROGRESS);
        setNewTodoList(StatusEnum.IN_PROGRESS, id);
      } else {
        setTaskStatus(StatusEnum.TODO);
        setNewTodoList(StatusEnum.TODO, id);
      }
    }
  }, [currentDoc, id, setNewTodoList]);

  const getCurrentStatus = currentDoc?.status ? currentDoc.status : taskStatus;
  const buttonStyles = getButtonStyles(getCurrentStatus as StatusEnum);

  return (
    <Box position="relative">
      <Menu fixed={false} offset={[2, 3]}>
        <MenuButton as={Flex as any}>
          <Flex alignItems="center" maxW="160px">
            <Button
              bg={buttonStyles.bg}
              color={buttonStyles.color}
              borderRadius="64px"
              px="20px"
              py="8px"
              mr={2}
              fontSize="15px"
              fontWeight="600"
              h="2.1rem"
              w="100%"
              isLoading={isLoading}
              loadingText="Updating"
              border="1px solid white"
              data-testid="status-dropdown-select-button"
            >
              {actionBtnLabel}
            </Button>
            <MdArrowForwardIos style={{ transform: 'rotate(90deg)' }} />
          </Flex>
        </MenuButton>
        <MenuList borderColor="blue.30" fontSize="14px">
          <MenuItem
            fontWeight="600"
            onClick={() => updateCandidateRecruiterAction(StatusEnum.TODO)}
            disabled={taskStatus === StatusEnum.TODO}
            data-testid="todo-button"
          >
            {t('toDo')}
          </MenuItem>
          <MenuItem
            fontWeight="600"
            onClick={() => updateCandidateRecruiterAction(StatusEnum.IN_PROGRESS)}
            disabled={taskStatus === StatusEnum.IN_PROGRESS}
            data-testid="in-progress-button"
          >
            {t('inProgress')}
          </MenuItem>
          <MenuItem
            fontWeight="600"
            onClick={() => updateCandidateRecruiterAction(StatusEnum.COMPLETED)}
            disabled={taskStatus === StatusEnum.COMPLETED}
            data-testid="completed-button"
          >
            {t('completed')}
          </MenuItem>
        </MenuList>
      </Menu>
      {currentDoc?.recruiterName && (currentDoc.status === StatusEnum.IN_PROGRESS || currentDoc.status === StatusEnum.COMPLETED) && (
        <Flex justifyContent="center" alignItems="center" mt={2}>
          <Text
            color={colors.gray[500]}
            fontWeight={400}
            fontSize={12}
            isTruncated
            noOfLines={1}
            maxWidth="25ch"
            data-testid="recruiter-name"
          >
            {currentDoc?.recruiterName}
          </Text>
        </Flex>
      )}
    </Box>
  );
});
